import type { ScholarshipPlayer } from '../../../../types/scholarships';
import type { CardAppearance } from './depthChartPositions';
import { isPitcherPosition, type PitcherHand } from './rosterPalette';

export interface PitchersByThrows {
  RHP: CardAppearance[];
  LHP: CardAppearance[];
}

/** Stable by-name order (last, first, id) for secondary entries, which carry no sort_order. */
const byName = (a: ScholarshipPlayer, b: ScholarshipPlayer): number => {
  if (a.lastName !== b.lastName) return a.lastName < b.lastName ? -1 : 1;
  if (a.firstName !== b.firstName) return a.firstName < b.firstName ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
};

/**
 * Buckets pitchers by hand from the filtered player list, as per-appearance entries.
 * Each hand lists PRIMARY pitchers first (server sort_order), then any SECONDARY entries
 * (players whose secondaryPosition is that hand — e.g. a primary RHP with secondary LHP),
 * name-sorted and read-only. A secondary entry can be any primary: a primary RHP who also
 * throws left, or a two-way player whose primary is a field position (SS) and secondary is a
 * hand. The list is assumed pre-sorted by the server's GET response order.
 *
 * Use this INSTEAD OF bucketPlayersIntoCards, which intentionally drops RHP/LHP.
 */
export const bucketPitchersByThrows = (
  players: ScholarshipPlayer[],
): PitchersByThrows => {
  const bucket = (hand: 'RHP' | 'LHP'): CardAppearance[] => {
    const primaries: CardAppearance[] = players
      .filter((p) => p.position === hand)
      .map((player) => ({ player, appearance: 'primary' as const }));
    const secondaries: CardAppearance[] = players
      .filter((p) => p.secondaryPosition === hand)
      .sort(byName)
      .map((player) => ({ player, appearance: 'secondary' as const }));
    return [...primaries, ...secondaries];
  };
  return { RHP: bucket('RHP'), LHP: bucket('LHP') };
};

export interface StaffHandRows {
  /** Primary pitchers at this hand, in the server's board order. Draggable. */
  primary: ScholarshipPlayer[];
  /**
   * Two-way players listed here by their secondary position, name-sorted. They carry no order at
   * this hand (the reorder endpoint is per primary position), so they sit below the staff.
   */
  twoWay: ScholarshipPlayer[];
}

/** The Pitching Staff tab's two tables, one row per player. */
export const pitchingStaffByHand = (
  players: readonly ScholarshipPlayer[],
): Record<PitcherHand, StaffHandRows> => {
  const rows = (hand: PitcherHand): StaffHandRows => ({
    primary: players.filter((p) => p.position === hand),
    twoWay: players
      .filter((p) => !isPitcherPosition(p.position) && p.secondaryPosition === hand)
      .sort(byName),
  });
  return { LHP: rows('LHP'), RHP: rows('RHP') };
};
